import {
  AccountToOrganizationMembershipStatus,
  ComplianceProgramParticipantStatus,
  ComplianceProgramRecurrence,
  GetComplianceProgramProgressQuery,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { ManagedTableColumn } from "@components/table/managed-table";
import { Tooltip } from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { getUrlUsingTypename } from "@helpers/helpers";

import ComplianceProgramParticipantRowActions from "./compliance-program-participant-row-actions";
import {
  complianceProgramReportingTableColNameClassName,
  complianceProgramReportingTableColsClassName,
} from "./compliance-program-progress-constants";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
} from "./compliance-program-progress-entity-table";
import ProgramProgressPill from "./program-progress-pill";

/**
 * The columns for one person's row. Each step cell is a pill with that person's status.
 */
export const useComplianceProgramProgressUserColumns = ({
  cols,
  complianceProgram,
}: {
  cols: ComplianceProgramReportingEntityTableCol[];
  complianceProgram: NonNullable<
    GetComplianceProgramProgressQuery["complianceProgram"]
  >;
}): ManagedTableColumn<ComplianceProgramReportingEntityTableParticipantStatusWithNominations>[] => {
  const label = useLabel();
  // The whole table is also shown to a manager looking at their reports, who cannot
  // change a roster. The menu is for the admins who can.
  const canUpdate = !!complianceProgram.canUpdate?.permission;

  return [
    {
      cellClassName: complianceProgramReportingTableColNameClassName,
      header: `Name`,
      render: (
        data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
      ) => {
        const isEngagementSurvey = !!complianceProgram.engagementSurveyTemplate;
        const assessmentDelivery = !isEngagementSurvey
          ? complianceProgram.assessmentDeliveries.edges.find(
              (edge) => edge?.node?.target?.id === data.user.id,
            )
          : complianceProgram.assessmentDeliveries.edges[0];
        return (
          <div className="min-w-0 flex flex-nowrap grow shrink-0 items-center justify-between gap-2">
            <div className="min-w-0 flex items-center gap-1">
              <div
                title={data.user.name}
                className={classNames(
                  "text-ellipsis overflow-hidden text-nowrap",
                  data.user.membershipStatus ===
                    AccountToOrganizationMembershipStatus.Inactive &&
                    "text-gray-300",
                )}
              >
                {data.user.name}
              </div>
              {data.user.membershipStatus ===
                AccountToOrganizationMembershipStatus.Inactive && (
                <Tooltip text="Deactivated">
                  <span className="shrink-0">
                    <TopicflowIcon
                      type="deactivated"
                      className="text-gray-300 h-5 w-5"
                    />
                  </span>
                </Tooltip>
              )}
            </div>
            {/* The row's own controls sit at the end of the name, where the Reviews
                        tab puts them, so they stay in view while the steps scroll sideways.
                        Each slot is held on every row so the icons line up down the column. */}
            <div className="flex items-center gap-1 shrink-0">
              {/* The link to the package, on the rows that have one. A greyed-out
                          icon on every row of a review nobody has finished yet is noise: an
                          absent link says "no package" just as clearly. */}
              {!isEngagementSurvey && (
                <div className="w-[32px] shrink-0">
                  {assessmentDelivery?.node && (
                    <Button
                      theme={buttonTheme.iconGray}
                      icon={TopicflowIconMap.externalLink}
                      to={getUrlUsingTypename(assessmentDelivery.node)}
                      tooltip={`Go to ${label("review", {
                        capitalize: true,
                      })} Package`}
                    />
                  )}
                </div>
              )}
              {canUpdate && (
                <div className="w-[32px] shrink-0">
                  <ComplianceProgramParticipantRowActions
                    complianceProgramId={complianceProgram.id}
                    userId={data.user.id}
                    userName={data.user.name}
                    excused={data.excused}
                    excusedReason={data.excusedReason}
                    recurs={
                      complianceProgram.recurrence !==
                      ComplianceProgramRecurrence.None
                    }
                  />
                </div>
              )}
            </div>
          </div>
        );
      },
      sortKey: "name",
      value: (
        data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
      ) => {
        return data.user.name;
      },
    },
    ...cols.map((col) => ({
      cellClassName: complianceProgramReportingTableColsClassName,
      header: col.label,
      sortKey: col.keyName,
      value: (
        data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
      ) =>
        data[col.keyName] === ComplianceProgramParticipantStatus.Complete
          ? 4
          : data[col.keyName] === ComplianceProgramParticipantStatus.InProgress
            ? 3
            : data[col.keyName] ===
                ComplianceProgramParticipantStatus.NotStarted
              ? 2
              : 1,
      render: (
        data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
      ) => {
        return (
          <ProgramProgressPill
            col={col}
            participantStatus={data}
            status={data[col.keyName]}
            complianceProgram={complianceProgram}
            className="hover:brightness-95"
          />
        );
      },
    })),
  ];
};
